import { CommonModule } from '@angular/common';
import { Component, computed, effect, OnDestroy, OnInit, signal, WritableSignal, viewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BadgeComponent } from '../../../../shared/components/badge/badge.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { TabComponent } from '../../../../shared/components/tabs/tab/tab.component';
import { TabsComponent } from '../../../../shared/components/tabs/tabs.component';
import { TagComponent } from '../../../../shared/components/tag/tag.component';
import { RideStatusType } from '../../../../shared/enums/rideStatusType';
import { isCameraDialogEnabled as checkCameraDialogEnabled } from '../../../../shared/utilities/utility-functions';
import { RideStopStatus } from '../../../../shared/enums/rideStopStatusType';
import { RideDocumentDto } from '../../../../shared/interfaces/ride-firestore';
import { RideListItem } from '../../../../shared/interfaces/ride-list-item';
import { IStep, IStepCargoes } from '../../../../shared/interfaces/step.interface';
import { StateService } from '../../../../shared/services/state.service';
import { ToggleMenuService } from '../../../../shared/services/toggle-menu.service';
import { AccordionModule } from 'primeng/accordion';
import { MenuItem, MenuItemCommandEvent, MessageService, TooltipOptions } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { Menu, MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { RideListItemComponent } from '../../components/ride-list-item/ride-list-item.component';
import { CancelTransportationDialogComponent } from '../../components/cancel-transportation-dialog/cancel-transportation-dialog.component';
import { ChangePassengerStatusDialogComponent } from '../../components/change-passenger-status-dialog/change-passenger-status-dialog.component';
import { ResetStopDialogComponent } from '../../components/reset-stop-dialog/reset-stop-dialog.component';
import { RideTimelineTooltipComponent } from '../../components/ride-timeline-tooltip/ride-timeline-tooltip.component';
import { ActionDialogService } from '../../services/action-dialog.service';
import { DriverDetailsCacheService } from '../../services/driver-details-cache.service';
import { MapRideServiceService } from '../../services/map-ride-service.service';
import { RideActionsService } from '../../services/ride-actions.service';
import { RideUpdatesService } from '../../services/ride-updates.service';
import { RideActionsDialogComponent } from './ride-actions-dialog/ride-actions-dialog.component';
import { RideActionsComponent } from './ride-actions/ride-actions.component';
import { cargoActionType } from '../../../../shared/enums/rideStopCargoStatus';
import { Common, PassengerType, RideType, StopPlaceType } from '../../../../shared/enums/common';
import { EnvConfigService } from '../../../../shared/services/env-config.service';
import { AuthService } from '../../../../core/auth/services/auth.service';
import { UserRole } from '../../../../core/auth/enums/userRoles';
import { StudentSchedule } from './student-schedule/student-schedule';
import { DrawerModule } from 'primeng/drawer';
import { RequestBuilderService } from '../../../../shared/services/request-builder.service';
import { StudentScheduleComponent } from './student-schedule/student-schedule.component';
import { RideDetailsMapProComponent } from '../../components/ride-details-map-pro/ride-details-map-pro.component';
import { QueryConstraint, where } from 'firebase/firestore';
import { FireStoreService } from '../../../../shared/services/firestore.service';
import { LoaderService } from '../../../../core/components/loader/loader.service';
import { CameraStatus, CameraStatusService } from '../../services/camera-status.service';
import { SingleMapService } from '../../../../shared/services/single-map.service';

interface Action {
  label: string | undefined;
  icon: string | undefined;
  actionKey: string;
  isDisabled: boolean;
  isVisible: boolean;
}

@Component({
  selector: 'app-ride-details',
  standalone: true,
  imports: [
    CommonModule,
    RideListItemComponent,
    ButtonComponent,
    TagComponent,
    AvatarModule,
    TabsComponent,
    TabComponent,
    AccordionModule,
    BadgeComponent,
    TooltipModule,
    RideTimelineTooltipComponent,
    MenuModule,
    DynamicDialogModule,
    RideDetailsMapProComponent,
    RideActionsComponent,
    DrawerModule,
    StudentScheduleComponent,
  ],
  providers: [DialogService],
  templateUrl: './ride-details.component.html',
  styleUrl: './ride-details.component.scss',
})
export class RideDetailsComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dialogService = inject(DialogService);
  private readonly stateService = inject(StateService);
  private readonly mapRideService = inject(MapRideServiceService);
  private readonly messageService = inject(MessageService);
  private readonly actionDialogService = inject(ActionDialogService);
  private readonly rideActionsService = inject(RideActionsService);
  private readonly toggleMenuService = inject(ToggleMenuService);
  private readonly driverDetailsCacheService = inject(DriverDetailsCacheService);
  private readonly env = inject(EnvConfigService);
  private readonly authService = inject(AuthService);
  private readonly requestBuilder = inject(RequestBuilderService);
  private readonly rideUpdatesService = inject(RideUpdatesService);
  private readonly firestoreService = inject(FireStoreService);
  private readonly loaderService = inject(LoaderService);
  private readonly cameraStatusService = inject(CameraStatusService);
  private readonly singleMapService = inject(SingleMapService);

  currentRide: WritableSignal<RideListItem> = signal({} as RideListItem);
  currentDocRide: RideDocumentDto = {} as RideDocumentDto;
  accordionActiveIndex: number | undefined = 0;

  cargoActionType = signal(cargoActionType);
  rideType = signal(RideType);
  stopPlaceType = signal(StopPlaceType);
  passengerType = signal(PassengerType);
  studentSchedule: StudentSchedule | null = null;
  avatarUrl: string | null = null;
  studentScheduleOpen = false;

  driverAvailability = signal(false);
  isClient = false;
  isAdminRole = localStorage.getItem('role')?.toLowerCase() == UserRole.Admin;
  readonly inlineMapInstance = viewChild.required<RideDetailsMapProComponent>('inlineRideMap');

  subs: Subscription[] = [];
  favoriteActions: Action[] = [];
  appSetting = this.authService.appSetting();

  currentRole = (localStorage.getItem('role') ?? '').toLowerCase();
  inProgressStatus = RideStatusType.InProgress;
  LiveTrackingStatuses = [RideStatusType.InProgress, RideStatusType.Completed, RideStatusType.NoShow, RideStatusType.CanceledLate, RideStatusType.CanceledInAdvance];
  rideStopMenuItems = signal<MenuItem[] | undefined>([]);
  rideStatusType = RideStatusType;
  tooltipOptions: TooltipOptions = {
    showDelay: 250,
    tooltipEvent: 'hover',
    tooltipPosition: 'bottom',
  };
  setRideStopMenuItems(e: any, stop: IStep, stopMenuRef: any, index: number) {
    const showResetStop = stop.statusId == RideStopStatus.Arrived || stop.statusId == RideStopStatus.Visited;
    this.rideStopMenuItems.set([
      {
        label: 'Reset Stop',
        command: e => {
          this.openResetStopDialog(stop, index, `Ride Stop Reset Successfully!`);
        },
        disabled: !showResetStop,
        visible: this.appSetting.hasResetStopPermission,
      },
      {
        label: 'View Activity Log',
        command: e => {
          this.rideActionsService.WaypointsActivityLogDialog(this.dialogService, stop.originalRideId, stop.stopId, this.currentRide().rideTimeline, this.currentRide().timezoneOffsetInSeconds);
        },
      },
    ]);

    this.openMenu(e, stopMenuRef);
  }

  passengerMenuItems = signal<MenuItem[] | undefined>([]);

  openMenu(event: Event, menu: Menu, btn?: HTMLElement) {
    btn?.classList.add('active-menu-btn');
    this.toggleMenuService.openMenu(menu, event);
  }

  RemoveBtnActiveClass(btn: HTMLElement) {
    console.log(btn);
    btn.classList.remove('active-menu-btn');
  }

  closeMenu() {
    this.toggleMenuService.closeMenu();
  }

  isActiveMenu(menu: Menu) {
    return this.toggleMenuService.isActiveMenu(menu);
  }

  openActionsDialog() {
    const dialogConfig = {
      showHeader: false,
      width: '90vw',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      data: {
        favoriteActions: this.favoriteActions,
        rideActionsMap: this.rideActionsMap,
        actionsMenuItems_1: this.actionsMenuItems_1,
        actionsMenuItems_2: this.actionsMenuItems_2,
        actionsMenuItems_3: this.actionsMenuItems_3,
      },
    };
    this.dialogService.open(RideActionsDialogComponent, dialogConfig);
  }

  hasCancelTransportationPermission = this.appSetting.hasCancelTransportationPermission;
  hasChangeStopCargoStatus = this.appSetting.hasChangeStopCargoStatus;
  setPassengerMenu(e: any, stop: IStep, passenger: IStepCargoes, passengerMenuRef: any) {
    //note: oData is the stop data, rowData is the stop cargo
    const showChangeStatus =
      this.appSetting.hasChangeStopCargoStatus &&
      this.currentDocRide?.ride_status_id != RideStatusType.None &&
      this.currentDocRide?.ride_status_id != RideStatusType.NoShow &&
      passenger.statusId != RideStatusType.CanceledDueToHoliday &&
      passenger.statusId != RideStatusType.CanceledInAdvance &&
      passenger.statusId != RideStatusType.CanceledLate &&
      !(passenger.actionType == cargoActionType.DropOff && passenger.statusId == RideStatusType.NoShow) &&
      stop.statusId != RideStopStatus.Visited &&
      stop.statusId != RideStopStatus.None &&
      stop.statusId != RideStopStatus.Skipped;

    const showCancelTransportation =
      this.appSetting.hasCancelTransportationPermission &&
      passenger.actionType == cargoActionType.PickUp &&
      passenger.statusId == RideStatusType.None &&
      this.currentDocRide?.ride_status_id != RideStopStatus.Visited &&
      (this.currentDocRide?.ride_status_id == RideStatusType.None || this.currentDocRide?.ride_status_id == RideStatusType.InProgress) &&
      !this.currentDocRide.ride_cargoes?.find(c => c.original_ride_cargo_id == passenger.rideCargoId)?.is_attendant;

    this.passengerMenuItems.set([
      {
        label: 'Cancel Transportation',
        command: e => {
          this.openPassengerStatusDialog('cancelTransportation', passenger, `Transportation status changed successfully`, stop);
        },
        disabled: !showCancelTransportation,
        visible: this.appSetting.hasCancelTransportationPermission,
      },
      {
        label: 'Change Status',
        command: e => {
          this.openPassengerStatusDialog('changeStatus', passenger, `Transportation status changed successfully`, stop);
        },
        visible: showChangeStatus,
      },
    ]);

    this.openMenu(e, passengerMenuRef);
  }

  rideActionsMap: { [key: string]: { command: Function; isVisibleFunction: Function; isDisabledFunction: Function } } = {
    rideActivity: {
      command: () => this.rideActionsService.openRideActivityDialog(this.currentRide(), this.currentDocRide, this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewRideActivity(),
      isDisabledFunction: () => false,
    },
    changeRideTime: {
      command: () => this.rideActionsService.openChangeRideTimeDialog(this.currentRide(), this.currentDocRide, this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewChangeRideTimeButton(this.currentRide()),
      isDisabledFunction: () => !this.rideActionsService.viewChangeRideTimeButton(this.currentRide()),
    },
    sendRideOffer: {
      command: () => this.rideActionsService.openSendRideOfferDialog(this.currentRide(), this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewSendRideOffer(),
      isDisabledFunction: () => this.rideActionsService.disableSendRideOffer(this.currentRide()),
    },
    autoAcceptOffer: {
      command: () => this.rideActionsService.openAutoAcceptRideDialog(this.currentRide(), this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewAutoAcceptRideButton(this.currentRide()),
      isDisabledFunction: () => !this.rideActionsService.viewAutoAcceptRideButton(this.currentRide()),
    },
    completeRide: {
      command: () => this.rideActionsService.openCompleteRideDialog(this.currentRide(), this.currentDocRide, this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewCompleteRide(),
      isDisabledFunction: () => true,
    },
    resetRide: {
      command: () => this.rideActionsService.openResetRideDialog(this.currentRide(), this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewResetRide(),
      isDisabledFunction: () => this.rideActionsService.disableResetRide(this.currentRide()),
    },
    ignoreDriverDoubleBooked: {
      command: () => this.rideActionsService.openIgnoreDriverDoubleBookedDialog(this.currentRide(), this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewIgnoreDoubleBooked(),
      isDisabledFunction: () => this.rideActionsService.disableIgnoreDoubleBooked(this.currentRide()),
    },
    ignoreMonitorDoubleBooked: {
      command: () => this.rideActionsService.openIgnoreMonitorDoubleBookedDialog(this.currentRide(), this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewIgnoreMonitorDoubleBooked(),
      isDisabledFunction: () => this.rideActionsService.disableIgnoreMonitorDoubleBooked(this.currentRide()),
    },
    unAssignDriver: {
      command: () => this.rideActionsService.openUnassignDriverDialog(this.currentRide(), this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewUnassignDriver(),
      isDisabledFunction: () => this.rideActionsService.disableUnassignDriver(this.currentRide()),
    },
    assignSubDriver: {
      command: () => {
        if (this.currentRole == UserRole.Provider) {
          return this.rideActionsService.openProviderAssignSubDriverDialog(this.currentRide(), this.currentDocRide, this.dialogService, this.messageService);
        } else {
          return this.rideActionsService.openAssignSubDriverDialog(this.currentRide(), this.currentDocRide, this.dialogService, this.messageService);
        }
      },
      isVisibleFunction: () => this.rideActionsService.viewAssignSubDriverButton(this.currentRide()),
      isDisabledFunction: () => !this.rideActionsService.viewAssignSubDriverButton(this.currentRide()),
    },
    findDriver: {
      command: () => this.rideActionsService.openFindDriverDialog(this.currentRide(), this.currentDocRide, this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewFindDriverButton(this.currentRide()),
      isDisabledFunction: () => !this.rideActionsService.viewFindDriverButton(this.currentRide()),
    },
    addPayoutAdjustment: {
      command: () => this.rideActionsService.openPayoutAdjustmentDialog(this.currentRide(), this.currentDocRide, this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewAddPayoutAdjustmentButton(this.currentRide()),
      isDisabledFunction: () => !this.rideActionsService.viewAddPayoutAdjustmentButton(this.currentRide()),
    },
    sendDelayNotification: {
      command: () => this.rideActionsService.openDelayNotificationDialog(this.currentRide(), this.currentDocRide, this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewSendDelayNotification(),
      isDisabledFunction: () => false,
    },
    addIncidentReport: {
      command: () => this.rideActionsService.openIncidentReportDialog(this.currentRide(), this.currentDocRide, this.dialogService, this.messageService),
      isVisibleFunction: () => this.rideActionsService.viewAddIncidentReport(),
      isDisabledFunction: () => false,
    },
    waypointsMap: {
      command: () => {
        this.openMapDialog();
      },
      isVisibleFunction: () => true,
      isDisabledFunction: () => false,
    },
    driverInfo: {
      command: () => this.actionDialogService.openContractorDialog(this.dialogService, this.messageService, this.currentRide(), this.currentDocRide),
      isVisibleFunction: () => true,
      isDisabledFunction: () => false,
    },
  };

  actionsMenuItems_1: MenuItem[] = [];

  actionsMenuItems_2: MenuItem[] = [];

  actionsMenuItems_3: MenuItem[] = [];

  cameraId = signal<string>('');
  cameraOnlineStatus = signal<boolean>(false);
  lastSyncDate = signal<Date | null>(null);
  partnerBotId = signal(Common.PartnerBotId);

  // Computed signal to determine if camera dialog should be enabled based on ride status and time
  isCameraDialogEnabled = computed(() => {
    const currentRide = this.currentRide();
    return checkCameraDialogEnabled(currentRide, currentRide?.JobStatusId);
  });

  constructor() {
    effect(
      () => {
        this.mapRideService.TimezoneDropdownValue();
        if (this.currentDocRide && Object.keys(this.currentDocRide).length > 0) {
          this.currentRide.set(this.mapRideService.getMappedItem(this.currentDocRide) || {});
          this.isClient = localStorage.getItem('role')?.toLowerCase() == UserRole.Client;
        }
      },
      {
        allowSignalWrites: true,
      },
    );

    // Emit ride updates when currentRide changes
    effect(() => {
      const currentRideData = this.currentRide();
      if (currentRideData?.id) {
        this.rideUpdatesService.emitRideUpdate(String(currentRideData.id), currentRideData);
      }
    });
  }

  ngOnInit() {
    this.activatedRoute.params.subscribe(params => {
      // Exit Street View when navigating between rides
      this.singleMapService.exitStreetView();

      this.accordionActiveIndex = 0;
      this.getCurrentRide(params['id']);
    });

    // Subscribe to live camera status by driver id (hide when PartnerBot/no driver)
    const driverId = this.currentRide()?.driverDetails?.id;
    if (driverId && driverId != this.partnerBotId()) {
      this.cameraStatusService.watchStatus(driverId).subscribe((status: CameraStatus) => {
        this.cameraId.set(status.camera_id || '');
        this.cameraOnlineStatus.set(status.camera_online_status || false);
        this.lastSyncDate.set(status.last_sync_date || null);
      });
    }
  }

  viewStudentSchedule(passenger: IStepCargoes) {
    console.log(passenger);
    // Get this the student id from the ride_cargoes
    const studentId = this.currentDocRide.ride_cargoes?.find(rc => rc.original_ride_cargo_id == passenger.rideCargoId)?.student_id ?? 0;
    this.requestBuilder.get(`stop-cargoes/student-schedule/${studentId}`).subscribe({
      next: res => {
        console.log(res);
        this.studentScheduleOpen = true;
        this.studentSchedule = res.data;
      },
      error: err => {
        console.log(err);
      },
    });
    this.requestBuilder.get(`stop-cargoes/student-avatar/${studentId}`).subscribe({
      next: res => {
        this.avatarUrl = res.message?.toLowerCase() == 'success' ? null : res.message;
      },
      error: err => {
        console.log(err);
      },
    });
  }

  getCurrentRide(id: number) {
    this.subs.push(
      this.stateService.displayedRides$.subscribe(rides => {
        // If the ride details page was loaded directly without loading the rides list => return to rides view
        if (rides.length == 0) {
          if (this.env.getConfig().isRunAsLibrary) {
            this.router.navigateByUrl(`/dispatch/monitoring-center`);
          } else {
            this.router.navigateByUrl(`/monitoring-center`);
          }
        } else {
          const currentDocRide: RideDocumentDto | undefined = rides.find(r => r.original_ride_id == id);

          if (currentDocRide) {
            this.handleRideData(currentDocRide);
          } else {
            // Ride not in state service, fetch from Firestore
            this.loaderService.show();
            const filters: QueryConstraint[] = [where('original_ride_id', '==', Number(id))];
            this.firestoreService
              .getDataFromCollection(this.env.getConfig().ridesCollection, filters)
              .then((res: any) => {
                console.log(res);
                if (res[0]) {
                  const currentDocRide = res[0].data as RideDocumentDto;
                  this.handleRideData(currentDocRide);
                  this.loaderService.hide();
                }
              })
              .catch(err => {
                this.loaderService.hide();
                console.log(err);
              });
          }
        }
      }),
    );
  }

  private handleRideData(currentDocRide: RideDocumentDto = this.currentDocRide) {
    this.currentDocRide = currentDocRide;

    this.currentRide.set(this.mapRideService.getMappedItem(currentDocRide) || {});
    this.GetPartnerAvailability(this.currentRide().driverDetails!.id);

    this.favoriteActions = JSON.parse(localStorage.getItem('favoriteActions') ?? '[]') ?? [];
    this.favoriteActions = this.favoriteActions.map(a => {
      return {
        ...a,
        isVisible: this.rideActionsMap[a.actionKey].isVisibleFunction(),
        isDisabled: this.rideActionsMap[a.actionKey].isDisabledFunction(),
      };
    });
    this.setMenus();
  }

  setMenus() {
    this.actionsMenuItems_1 = this.createFirstActionMenu();
    this.actionsMenuItems_2 = this.createSecondActionMenu();
    this.actionsMenuItems_3 = this.createThirdActionMenu();
  }

  private createFirstActionMenu() {
    return [
      this.createMenuItem('Ride Activity', 'icon-steering-wheel-solid', 'rideActivity', false, this.rideActionsService.viewRideActivity()),
      this.createMenuItem('Change Ride Time', 'icon-clock-user-solid', 'changeRideTime', false, this.rideActionsService.viewChangeRideTimeButton(this.currentRide()), !this.rideActionsService.viewChangeRideTimeButton(this.currentRide())),
      { separator: true },
      this.createMenuItem('Send Ride Offer', 'icon-hand-coins-solid', 'sendRideOffer', this.rideActionsService.disableSendRideOffer(this.currentRide()), this.rideActionsService.viewSendRideOffer()),
      this.createMenuItem('Auto Accept Offer', 'icon-user-circle-check-solid', 'autoAcceptOffer', false, this.rideActionsService.viewAutoAcceptRideButton(this.currentRide()), !this.rideActionsService.viewAutoAcceptRideButton(this.currentRide())),
      { separator: true },
      this.createMenuItem('Complete Ride', 'icon-check-circle-solid', 'completeRide', true, this.rideActionsService.viewCompleteRide()),
      this.createMenuItem('Reset Ride', 'icon-arrow-clockwise-solid', 'resetRide', this.rideActionsService.disableResetRide(this.currentRide()), this.rideActionsService.viewResetRide()),
      this.createMenuItem('Ignore Driver 2x Booked', 'icon-ticket-solid', 'ignoreDriverDoubleBooked', this.rideActionsService.disableIgnoreDoubleBooked(this.currentRide()), this.rideActionsService.viewIgnoreDoubleBooked()),
      this.createMenuItem('Ignore Monitor 2x Booked', 'icon-user-rectangle-solid', 'ignoreMonitorDoubleBooked', this.rideActionsService.disableIgnoreMonitorDoubleBooked(this.currentRide()), this.rideActionsService.viewIgnoreMonitorDoubleBooked()),
      { separator: true },
      {
        label: 'Delete Ride',
        styleClass: 'delete-item',
        visible: this.rideActionsService.viewDeleteRide(),
        command: (e: MenuItemCommandEvent) => {
          this.rideActionsService.openDeleteRideDialog(this.currentRide(), this.dialogService, this.messageService);
        },
      },
    ];
  }

  private createSecondActionMenu() {
    return [
      this.createMenuItem('Un-Assign Driver', 'icon-user-circle-minus-solid', 'unAssignDriver', this.rideActionsService.disableUnassignDriver(this.currentRide()), this.rideActionsService.viewUnassignDriver()),
      this.createMenuItem('Assign Sub-Driver', 'icon-user-circle-plus-solid', 'assignSubDriver', false, this.rideActionsService.viewAssignSubDriverButton(this.currentRide()), !this.rideActionsService.viewAssignSubDriverButton(this.currentRide())),
      { separator: true },
      this.createMenuItem('Find Driver', 'icon-binoculars-solid', 'findDriver', false, this.rideActionsService.viewFindDriverButton(this.currentRide()), !this.rideActionsService.viewFindDriverButton(this.currentRide())),
      this.createMenuItem(
        'Add Payout Adjustment',
        'icon-currency-dollar-solid',
        'addPayoutAdjustment',
        false,
        this.rideActionsService.viewAddPayoutAdjustmentButton(this.currentRide()),
        !this.rideActionsService.viewAddPayoutAdjustmentButton(this.currentRide()),
      ),
      { separator: true },
      this.createMenuItem('Send Delay Notifications', 'icon-hourglass-solid', 'sendDelayNotification', false, this.rideActionsService.viewSendDelayNotification()),
    ];
  }

  private createThirdActionMenu() {
    return [this.createMenuItem('Add Incident Report', 'icon-warning-solid', 'addIncidentReport', false, this.rideActionsService.viewAddIncidentReport()), this.createMenuItem('Waypoints Map', 'icon-line-segments-solid', 'waypointsMap', false, true)];
  }

  private createMenuItem(label: string, actionIcon: string, actionKey: string, isDisabled: boolean, isVisible: boolean, disabled: boolean = false) {
    return {
      label,
      icon: this.isFavoriteAction(label) ? 'icon-star-solid' : 'icon-star',
      iconClass: this.isFavoriteAction(label) ? 'feedback-warning-fg-color' : 'icon-light-color',
      visible: isVisible,
      disabled,
      actionIcon,
      actionKey,
      command: (e: MenuItemCommandEvent) => {
        this.toggleFavoriteAction({
          label: e.item?.label,
          icon: actionIcon,
          actionKey,
          isDisabled,
          isVisible,
        });

        e.item!.icon = this.getActionIcon(e.item?.icon, e.item?.iconClass).icon;
        e.item!.iconClass = this.getActionIcon(e.item?.icon, e.item?.iconClass).iconClass;
      },
    };
  }

  private GetPartnerAvailability(partnerId: number) {
    this.driverDetailsCacheService.getAvailabilityStatus(partnerId).subscribe(res => {
      this.driverAvailability.set(res);
    });
  }

  navigateToRideDetails(ride: RideListItem) {
    if (this.env.getConfig().isRunAsLibrary) {
      this.router.navigateByUrl(`/dispatch/monitoring-center/ride-details/${ride.id}`);
    } else {
      this.router.navigateByUrl(`/monitoring-center/ride-details/${ride.id}`);
    }
  }

  back() {
    if (this.env.getConfig().isRunAsLibrary) {
      this.router.navigateByUrl(`/dispatch/monitoring-center/rides`);
    } else {
      this.router.navigateByUrl(`/monitoring-center/rides`);
    }
  }

  toggleActiveAccordionTab(index: number) {
    this.accordionActiveIndex = this.accordionActiveIndex == index ? -1 : index;
    this.toggleMenuService.closeMenu();
  }

  accordionActiveIndexChange(index: any) {
    this.accordionActiveIndex = index;
  }

  private isFavoriteAction(actionLabel: string) {
    return this.favoriteActions.find(action => action.label?.toLowerCase() == actionLabel.toLowerCase());
  }

  private toggleFavoriteAction(action: Action) {
    let index!: number;
    const isFavoriteAction = this.favoriteActions.find((a, i) => {
      index = i;
      return a.label?.toLowerCase() == action.label?.toLowerCase();
    });
    if (isFavoriteAction) {
      this.favoriteActions.splice(index, 1);
      localStorage.setItem('favoriteActions', JSON.stringify(this.favoriteActions));
    } else {
      this.favoriteActions.push({
        label: action.label,
        icon: action.icon,
        actionKey: action.actionKey,
        isDisabled: action.isDisabled,
        isVisible: action.isVisible,
      });
      localStorage.setItem('favoriteActions', JSON.stringify(this.favoriteActions));
    }
  }

  private getActionIcon(currentIcon: string | undefined, currentIconClass: string) {
    let icon = currentIcon == 'icon-star' ? 'icon-star-solid' : 'icon-star';
    let iconClass = currentIconClass == 'icon-light-color' ? 'feedback-warning-fg-color' : 'icon-light-color';
    return { icon, iconClass };
  }

  openBlueprintDialog() {
    this.actionDialogService.openBlueprintDialog(this.dialogService, this.currentRide(), this.currentDocRide);
  }

  openMapDialog() {
    const mapDialogData = {
      dialogService: this.dialogService,
      docRide: this.currentDocRide,
      mappedRide: this.currentRide(),
      driverOnline: this.driverAvailability(),
      rideId: String(this.currentRide().id),
    };
    this.actionDialogService.openMapDialog(mapDialogData);
  }

  openContractorDialog() {
    this.actionDialogService.openContractorDialog(this.dialogService, this.messageService, this.currentRide(), this.currentDocRide);
  }

  openResetStopDialog(stop: IStep, index: number, onConfirmedMessage: string) {
    const dialogConfig = {
      showHeader: false,
      width: '640px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '960px': '400px',
        '575px': '90vw',
      },
      data: {
        id: this.currentRide()?.id,
        uuid: this.currentRide()?.uuid,
        stop,
        index,
      },
    };
    const ref = this.dialogService.open(ResetStopDialogComponent, dialogConfig)!;

    ref.onClose.subscribe(res => {
      if (res?.confirmed) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: onConfirmedMessage,
          life: 6000,
        });
      }
    });
  }

  openPassengerStatusDialog(action: string, passenger: any, onConfirmedMessage: string, stop?: any) {
    const dialogConfig = {
      showHeader: false,
      width: '640px',
      modal: true,
      styleClass: 'dispatch-portal-custom-dialog',
      breakpoints: {
        '960px': '400px',
        '575px': '90vw',
      },
      data: {
        uuid: this.currentRide()?.uuid,
        passenger,
        stop,
        timezoneOffsetInSeconds: this.currentRide().timezoneOffsetInSeconds,
        firstStopScheduledTime: this.currentRide().rideTimeline[0]?.scheduled_arrival_time,
      },
    };
    const ref = (action == 'changeStatus' ? this.dialogService.open(ChangePassengerStatusDialogComponent, dialogConfig) : this.dialogService.open(CancelTransportationDialogComponent, dialogConfig))!;

    ref.onClose.subscribe(res => {
      if (res?.confirmed) {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: onConfirmedMessage,
          life: 6000,
        });
      }
    });
  }

  public openCameraDialog() {
    const driver = this.currentDocRide?.ride_drivers?.find(d => !d.is_deleted);
    this.actionDialogService.openCameraDialog(driver?.driver_id ?? 0, this.cameraOnlineStatus(), this.lastSyncDate(), this.cameraStatusService, this.dialogService, this.currentRide());
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }
}
